import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { adminService, type ConnectReport } from '../../services/adminService';

/**
 * Where the connect time goes, and why calls ended.
 *
 * The backend has recorded all of this per call since the connect trace shipped
 * — seven timestamps and a snapshot of the agent pool at the moment routing
 * decided — and until now nothing displayed it. Answering "how long does a
 * merchant wait before an agent speaks" meant hand-querying the API.
 *
 * Two numbers here are the ones worth acting on, so they lead:
 *
 *  - **Dropped with an agent available** — a merchant was let go for "no agent"
 *    while the snapshot named someone the dialer counted as free. Non-zero
 *    means a routing fault, not a staffing one.
 *  - **Dropped to an unreachable agent** — the subset of those where the agent's
 *    browser could not have taken the call. This is the dead-air case.
 */

const RANGE_PRESETS = [
  { label: 'Last hour', hours: 1 },
  { label: 'Last 8 hours', hours: 8 },
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 24 * 7 },
] as const;

/** Plain-English names. The API's stage keys are column pairs, not labels. */
const STAGE_LABELS: Record<string, string> = {
  answer_to_lookup: 'Merchant answered → agent lookup',
  lookup_to_reserved: 'Agent lookup → agent reserved',
  reserved_to_bridge_request: 'Agent reserved → transfer sent',
  bridge_request_to_agent_answer: 'Transfer sent → agent picked up',
  answer_to_agent_answer: 'Merchant answered → agent picked up',
  agent_answer_to_audio: 'Agent picked up → two-way audio',
  answer_to_audio: 'Merchant answered → two-way audio',
};

/** The two end-to-end stages. Shown apart because they are the headline. */
const HEADLINE_STAGES = new Set(['answer_to_audio', 'answer_to_agent_answer']);

const REASON_LABELS: Record<string, string> = {
  CONNECTED: 'Connected to an agent',
  NO_AGENT_AVAILABLE: 'No agent available',
  AMD_CANCELLED: 'Ended by machine detection',
  AGENT_STATE_CHANGED: 'Reserved agent taken elsewhere',
  DUPLICATE_CONNECT: 'Over-dial surplus cancelled',
  CALLER_HUNG_UP: 'Merchant hung up waiting',
  BRIDGE_TIMEOUT: 'No agent answered in time',
  SYSTEM_CANCELLED: 'Cancelled by the system',
  CARRIER_CANCELLED: 'No answer / busy / rejected',
  AGENT_NOT_READY: 'Agent not ready',
  OTHER: 'Other',
  IN_FLIGHT: 'Still in progress',
};

const GOOD_REASONS = new Set(['CONNECTED']);
const BAD_REASONS = new Set([
  'NO_AGENT_AVAILABLE',
  'BRIDGE_TIMEOUT',
  'CALLER_HUNG_UP',
  'AGENT_STATE_CHANGED',
]);

function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function formatMs(value: number | null): string {
  if (value === null) return '—';
  if (value < 1000) return `${value}ms`;
  return `${(value / 1000).toFixed(2)}s`;
}

/** 1s is the target; 3s was the measured median before the connect work. */
function latencyTone(stage: string, p50: number | null): string {
  if (p50 === null || !HEADLINE_STAGES.has(stage)) return 'text-foreground';
  if (p50 <= 1000) return 'text-green-700 dark:text-green-400';
  if (p50 <= 3000) return 'text-orange-700 dark:text-orange-400';
  return 'text-red-700 dark:text-red-400';
}

const ConnectReportPage: React.FC = () => {
  const [from, setFrom] = useState(() =>
    toLocalInput(new Date(Date.now() - 24 * 60 * 60 * 1000))
  );
  const [to, setTo] = useState(() => toLocalInput(new Date()));
  const [campaignId, setCampaignId] = useState('');
  const [report, setReport] = useState<ConnectReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getConnectReport({
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        ...(campaignId.trim() ? { campaignId: campaignId.trim() } : {}),
      });
      setReport(data);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Could not load the connect report.';
      setError(message);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, campaignId]);

  useEffect(() => {
    void load();
    // Deliberately once on mount. Every other refresh is the operator's choice —
    // these queries scan a window of traces and should not fire on each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (hours: number) => {
    const now = new Date();
    setFrom(toLocalInput(new Date(now.getTime() - hours * 60 * 60 * 1000)));
    setTo(toLocalInput(now));
  };

  const headline = useMemo(
    () => (report?.latency ?? []).filter((s) => HEADLINE_STAGES.has(s.stage)),
    [report]
  );
  const steps = useMemo(
    () => (report?.latency ?? []).filter((s) => !HEADLINE_STAGES.has(s.stage)),
    [report]
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <ClockIcon className="h-6 w-6 text-primary" />
          Connect Report
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Human auto-dial only. Every traced call, from the moment the merchant answered to the
          moment they could hear an agent — and the reason each one ended.
        </p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs">
              <span className="block mb-1 text-muted-foreground">From</span>
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="block mb-1 text-muted-foreground">To</span>
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs">
              <span className="block mb-1 text-muted-foreground">Campaign (optional)</span>
              <input
                type="text"
                value={campaignId}
                placeholder="Campaign ID — blank for all"
                onChange={(e) => setCampaignId(e.target.value)}
                className="w-64 rounded border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <Button onClick={() => void load()} disabled={loading}>
              {loading ? 'Loading…' : 'Run report'}
            </Button>
            <div className="flex gap-1.5">
              {RANGE_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => applyPreset(p.hours)}
                  className="rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:border-primary"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Windows wider than 31 days are refused — these queries scan every trace in the range.
          </p>
        </CardContent>
      </Card>

      {error && (
        <Card>
          <CardContent className="p-4 text-sm text-red-700 dark:text-red-400">{error}</CardContent>
        </Card>
      )}

      {loading && !report && (
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      )}

      {report && (
        <>
          {/* The two numbers that say whether there is a bug at all. */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-muted-foreground">Calls traced</div>
                <div className="text-2xl font-bold tabular-nums">
                  {report.breakdown.totalTraced}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {report.breakdown.totalCancelled} ended without an agent
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-muted-foreground">Dropped with an agent available</div>
                <div
                  className={`text-2xl font-bold tabular-nums ${
                    report.lostWithAgentAvailable.count > 0
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-green-700 dark:text-green-400'
                  }`}
                >
                  {report.lostWithAgentAvailable.count}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  of {report.lostWithAgentAvailable.noAgentTotal} "no agent" drops
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-xs text-muted-foreground">
                  …whose WebRTC client was dead
                </div>
                <div
                  className={`text-2xl font-bold tabular-nums ${
                    report.unreachableAgentDrops.count > 0
                      ? 'text-orange-700 dark:text-orange-400'
                      : 'text-green-700 dark:text-green-400'
                  }`}
                >
                  {report.unreachableAgentDrops.count}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  Routed to a browser that could not ring
                </div>
              </CardContent>
            </Card>
          </div>

          {/* End-to-end, then the steps it is made of. */}
          <Card>
            <CardContent className="p-5">
              <h2 className="text-base font-semibold mb-1">How long the merchant waits</h2>
              <p className="text-xs text-muted-foreground mb-4">
                Median and 95th percentile across the window. The two figures below are
                end-to-end; the table breaks the same wait into its steps.
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-5">
                {headline.map((stage) => (
                  <div key={stage.stage} className="rounded-lg border border-border p-4">
                    <div className="text-xs text-muted-foreground">
                      {STAGE_LABELS[stage.stage] ?? stage.stage}
                    </div>
                    <div className="mt-1 flex items-baseline gap-4">
                      <div>
                        <span
                          className={`text-2xl font-bold tabular-nums ${latencyTone(
                            stage.stage,
                            stage.p50Ms
                          )}`}
                        >
                          {formatMs(stage.p50Ms)}
                        </span>
                        <span className="ml-1 text-[11px] text-muted-foreground">median</span>
                      </div>
                      <div>
                        <span className="text-base font-semibold tabular-nums">
                          {formatMs(stage.p95Ms)}
                        </span>
                        <span className="ml-1 text-[11px] text-muted-foreground">p95</span>
                      </div>
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {stage.samples} call{stage.samples === 1 ? '' : 's'}
                    </div>
                  </div>
                ))}
                {headline.length === 0 && (
                  <div className="text-sm text-muted-foreground">
                    No completed connects in this window.
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4 font-medium">Step</th>
                      <th className="py-2 pr-4 font-medium text-right">Median</th>
                      <th className="py-2 pr-4 font-medium text-right">p95</th>
                      <th className="py-2 font-medium text-right">Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.map((stage) => (
                      <tr key={stage.stage} className="border-b border-border/50">
                        <td className="py-2 pr-4">{STAGE_LABELS[stage.stage] ?? stage.stage}</td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          {formatMs(stage.p50Ms)}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">
                          {formatMs(stage.p95Ms)}
                        </td>
                        <td className="py-2 text-right tabular-nums text-muted-foreground">
                          {stage.samples}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h2 className="text-base font-semibold mb-1">How calls ended</h2>
              <p className="text-xs text-muted-foreground mb-4">
                Every traced call in the window, grouped by outcome.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4 font-medium">Outcome</th>
                      <th className="py-2 pr-4 font-medium text-right">Calls</th>
                      <th className="py-2 font-medium text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.breakdown.reasons.map((r) => (
                      <tr key={r.reason} className="border-b border-border/50">
                        <td
                          className={`py-2 pr-4 ${
                            GOOD_REASONS.has(r.reason)
                              ? 'text-green-700 dark:text-green-400'
                              : BAD_REASONS.has(r.reason)
                                ? 'text-red-700 dark:text-red-400'
                                : ''
                          }`}
                        >
                          {REASON_LABELS[r.reason] ?? r.reason}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">{r.count}</td>
                        <td className="py-2 text-right tabular-nums text-muted-foreground">
                          {(r.share * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                    {report.breakdown.reasons.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-4 text-sm text-muted-foreground">
                          No traced calls in this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h2 className="text-base font-semibold mb-1">Connected, but nobody there</h2>
              <p className="text-xs text-muted-foreground mb-4">
                Two failures that look identical to an agent and have opposite fixes.
              </p>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-xs text-muted-foreground">Machine reached an agent</dt>
                  <dd className="text-xl font-semibold tabular-nums">
                    {report.nobodyThere.amdFalsePositives}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Merchant gave up waiting</dt>
                  <dd className="text-xl font-semibold tabular-nums">
                    {report.nobodyThere.merchantHungUpBeforeBridge}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Connected calls</dt>
                  <dd className="text-xl font-semibold tabular-nums">
                    {report.nobodyThere.connectedTotal}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Not yet dispositioned</dt>
                  <dd className="text-xl font-semibold tabular-nums">
                    {report.nobodyThere.awaitingAgentTag}
                  </dd>
                </div>
              </dl>
              {report.nobodyThere.awaitingAgentTag > 0 && (
                <p className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground">
                  <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
                  <span>
                    {report.nobodyThere.awaitingAgentTag} connected call
                    {report.nobodyThere.awaitingAgentTag === 1 ? ' has' : 's have'} no agent tag
                    yet, so the machine-reached-an-agent count is a floor, not a total.
                  </span>
                </p>
              )}
            </CardContent>
          </Card>

          {report.lostWithAgentAvailable.callAttemptIds.length > 0 && (
            <Card>
              <CardContent className="p-5">
                <h2 className="text-base font-semibold mb-1">Calls to look at</h2>
                <p className="text-xs text-muted-foreground mb-3">
                  Dropped for "no agent" while the pool held someone the dialer counted as free.
                  Open any of these on its lead to see the full timing and the agent pool as it
                  stood at that instant.
                </p>
                <ul className="space-y-1 font-mono text-[11px] text-muted-foreground">
                  {report.lostWithAgentAvailable.callAttemptIds.slice(0, 25).map((id) => (
                    <li key={id}>{id}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ConnectReportPage;
